import {getPreferredUsername, Group, isClUser, NoCurrentUserError} from '@simpleinvest/front-end-auth';
import get from 'lodash.get';
import {capitalizeFirstLetter} from '@principal/text-formatters';
import startCase from 'lodash.startcase';
import {Logger, Category} from '@simpleinvest/front-end-logger';

const logger = new Logger({
  category: Category.Chat,
  fileName: 'chat.js',
});

const stateObj = () => ({
  caseSubject: 'New Support Request from Chat',
  caseStatus: 'New',
  caseOrigin: 'Chat',
  subOrigin: 'RobustWealth',
  topic: 'Robust Wealth',
  chatRecordType: '012610000007IB0AAM',
  buttonId: '57361000000XZAH',
  settings: {},
  client: {
    name: 'User',
    window: {
      width: 500,
      height: 500,
    },
  },
});

const mutations = {
  saveSettings(state, {chatSettings, eid}) {
    let eidForChat = '';

    if (chatSettings.active) {
      if (!eid) {
        throw new Error('"eid" not set.');
      }

      eidForChat = `${eid?.toUpperCase()}${capitalizeFirstLetter(Group.ClUser)}`;
    }

    const settings = {
      active: chatSettings.active,
      deployment: chatSettings.deployment,
      eid: eidForChat,
      endpoint: chatSettings.endpoint,
      org: chatSettings.org,
      recordType: chatSettings.recordType,
      token: '',
    };

    state.settings = {...settings};
  },
};

const gettersObj = {
  clientName: (_state, _getters, rootState) => {
    let ria = get(rootState, 'riaName', 'Unknown');

    ria = ria === 'Unknown' ? get(rootState, 'settings.ria.companyLegalName', 'Unknown') : ria;
    const name = `${get(rootState, 'user.current.first_name', 'User')} from ${ria}`;

    if (!get(rootState, 'onboarding.currentStep')) return name;

    return `${name} ${startCase(rootState.onboarding.currentStep)} Step`;
  },
};

const actions = {
  async getChatSettings({commit}, chatSettings) {
    try {
      const userIsClUser = await isClUser();

      if (chatSettings && userIsClUser) {
        const eid = await getPreferredUsername();

        commit('saveSettings', {
          chatSettings,
          eid,
        });
      }
    } catch (error) {
      if (!(error instanceof NoCurrentUserError)) {
        logger.addBreadcrumb({
          callingFuncName: 'getChatSettings',
          message: `getChatSettings error caught`,
        });
        logger.logException({error});
      }
    }
  },
  // eslint-disable-next-line require-await
  async init({state, getters, rootState}) {
    // eslint-disable-next-line unicorn/prefer-array-some
    if (!rootState.settings.enterprise.features.find((feature) => feature.toLowerCase() === 'chat')) {
      return;
    }

    const {liveagent} = window;

    // liveagent.enableLogging();
    /* Dynamic data */
    liveagent.addCustomDetail('CaseSubject', state.caseSubject);
    liveagent.addCustomDetail('CaseStatus', state.caseStatus);
    liveagent.addCustomDetail('CaseOrigin', state.caseOrigin);
    liveagent.addCustomDetail('ChatSubOrigin', state.subOrigin, true);
    liveagent.addCustomDetail('CaseTopic', state.topic, true);
    /* This is the record type for PAS
          Will be unique for every different case */
    liveagent.addCustomDetail('RecordType', state.settings.recordType);
    /* EID for the user */
    liveagent.addCustomDetail('EnterpriseID', `${state.settings.eid}Consumer`);
    /* Encrypted token goes here */
    liveagent.addCustomDetail('Token', state.settings.token);
    liveagent.addCustomDetail('ChatDeployment', state.settings.deployment);

    /* An auto-query that searches Contacts whose Contact_Distinct). If no result is found, it will not create a Contact record. */
    liveagent
      .findOrCreate('Case')
      .map('RecordTypeId', 'RecordType', false, false, true)
      .map('Subject', 'CaseSubject', false, false, true)
      .map('Status', 'CaseStatus', false, false, true)
      .map('Origin', 'CaseOrigin', false, false, true)
      .map('Chat_Sub_Origin__c', 'ChatSubOrigin', false, false, true)
      .map('Topic__c', 'CaseTopic', false, false, true)
      .saveToTranscript('CaseId')
      .showOnCreate();

    liveagent
      .findOrCreate('Contact')
      .map('Contact_Distinct_ID__c', 'EnterpriseID', true, true, false)
      .saveToTranscript('ContactId')
      .showOnCreate()
      .linkToEntity('ChatToken__c', 'Contact__c');

    liveagent
      .findOrCreate('ChatToken__c')
      .map('Visitor_Token__c', 'Token', false, false, true)
      .map('Deployment_Key_Setting__c', 'ChatDeployment', false, false, true)
      .map('Contact__c', 'Contact', false, false, true)
      .saveToTranscript('ChatToken__c');

    /* Sets the display name of the visitor and where at in the agent console when engaged in a chat */
    liveagent.setName(getters.clientName);
    /* Sets the width of the chat window to 500px */
    liveagent.setChatWindowWidth(state.client.window.width);
    /* Sets the height of the chat window to 500px */
    liveagent.setChatWindowHeight(state.client.window.height);
    liveagent.init(`https://d.${state.settings.endpoint}/chat`, state.settings.deployment, state.settings.org);
  },
  // eslint-disable-next-line require-await
  async start({state}) {
    const {liveagent} = window;

    liveagent.startChat(state.buttonId);
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  mutations,
  actions,
};
